import { useState, useEffect } from 'react';
import { Device } from '@capacitor/device';
import { getConfig, isPlatform } from '@ionic/react';

export interface DeviceDetails {
  platform: string;
  operatingSystem: string;
  osVersion: string;
  manufacturer: string;
  model: string;
  webViewVersion?: string;
}

export interface AppExitInstructions {
  type: 'faceID' | 'homeButton';
  steps: string[];
}

export interface DeviceInfo {
  deviceDetails?: DeviceDetails;
  isIPad: boolean;
  isIPhone: boolean;
  isIOS: boolean;
  isMacOS: boolean;
  isIonModeIOS: boolean;
  isIonModeMD: boolean;
  isLoading: boolean;
  appExitInstructions: AppExitInstructions;
}

function detectMacOS(): boolean {
  // macOS desktop: UA contains "Macintosh" but NOT an iPad in desktop-mode
  // (iPad in desktop mode: "Macintosh" + maxTouchPoints > 1, handled by detectIPad)
  return navigator.userAgent.includes('Macintosh') && !isPlatform('ipad');
}

// Which design language Ionic components render in, which is a separate
// question from what device this is: App.tsx picks the mode with
// isPlatform('android'), so desktop web and headless Electron run in ios mode
// while isIOS is false. Ionic resolves the mode once at startup and stores it
// in its config; fall back to App.tsx's own rule if that has not run yet.
function detectIonMode(): 'ios' | 'md' {
  const mode = getConfig()?.get('mode');
  if (mode === 'ios' || mode === 'md') {
    return mode;
  }
  return isPlatform('android') ? 'md' : 'ios';
}

function computeAppExitInstructions(): AppExitInstructions {
  const hasHomeBar = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-safe-area-bottom');

  const isModerniPhone = parseInt(hasHomeBar) > 0;

  if (isModerniPhone) {
    return {
      type: 'faceID',
      steps: [
        'Swipe up from the bottom edge and pause in the middle of the screen.',
        'Find the Snowball card.',
        'Swipe the card up and off the top to close it.',
      ],
    };
  } else {
    return {
      type: 'homeButton',
      steps: [
        'Double-click the circular Home button below your screen.',
        'Find the Snowball card.',
        'Swipe the card up and off the top to close it.',
      ],
    };
  }
}

export function useDeviceInfo(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = useState<DeviceDetails | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(true);
  const [appExitInstructions] = useState<AppExitInstructions>(
    computeAppExitInstructions
  );

  useEffect(() => {
    const loadDeviceInfo = async () => {
      try {
        const info = await Device.getInfo();
        setDeviceInfo(info as DeviceDetails);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load device info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDeviceInfo();
  }, []);

  const ionMode = detectIonMode();

  return {
    deviceDetails: deviceInfo,
    isIPad: isPlatform('ipad'),
    isIPhone: isPlatform('iphone'),
    isIOS: isPlatform('ios'),
    isMacOS: detectMacOS(),
    isIonModeIOS: ionMode === 'ios',
    isIonModeMD: ionMode === 'md',
    isLoading,
    appExitInstructions,
  };
}
